const CLIENT_PROPERTIES_KEY = 'client-properties';

interface ReactNativeWindow extends Window {
	ReactNativeWebView?: { postMessage(message: string): void };
}

/**
 * This a service for communicating with local-storage,
 * that also adds a bridge to React Natives
 * Async Storage when available and neccessary
 */
class ModelStorageService {

	/** This method sends the current local-storage state to React-Native */
	private async updateAsyncStorage(key: string, value: string, remove: boolean = false, clear: boolean = false) {
		const isIOS = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
		const rnWindow = window as unknown as ReactNativeWindow;
		if (isIOS && rnWindow.ReactNativeWebView) {
			rnWindow.ReactNativeWebView.postMessage(JSON.stringify({
				type: clear? 'CLEAR_STORAGE' : 'UPDATE_STORAGE',
				key,
				value,
				remove
			}));
		}
	}

	constructor() {
		// After being created the service will receive the
		// current async-storage values when sent from react-native
		const onStorageReceived = (event: Event) => {
			try {
				const [key, data] = (event as CustomEvent<string>).detail?.split('|||')
					?? ((event as Event & { data?: string }).data?.split('|||') || []);
				if (key && data) {
					window.localStorage.setItem(key, data);
				}
			}
			catch(e) {
				console.error("unable to receive storage", e);
			}
		}

		window.addEventListener('onStorageReceived', onStorageReceived);
	}

	public async setItem(key: string, value: string) {
		await this.updateAsyncStorage(key, value);
		return window.localStorage.setItem(key, value);
	}

	public getItem(key: string): string | null {
		return window.localStorage.getItem(key);
	}

	public removeItem(key: string): void {
		this.updateAsyncStorage(key, "", true);
		return window.localStorage.removeItem(key);
	}

	/** 
	 * This method clears all storage and reloads the view,
	 * keeping client-properties.
	 */
	public async clearAndReload() {
		// Save the current client properties
		const storedProperties = await this.getItem(CLIENT_PROPERTIES_KEY);
		// Clear all storaged data
		window.localStorage.clear();
		await this.updateAsyncStorage("", "", false, true);
		// Re-save client properties to local-storage only
		if (storedProperties) {
			window.localStorage.setItem(CLIENT_PROPERTIES_KEY, storedProperties);
		}
		// Reload the view
		window.location.reload();
	}

	/** This method makes sure that the correct client properties are available */
	public async verifyClientProperties(storedProperties? : string | null) {
		// console.warn("props before verification", __PROPS__);
		storedProperties = storedProperties || this.getItem(CLIENT_PROPERTIES_KEY);

		// Store the current properties if not yet set in local-storage only
		if (!storedProperties) {
			window.localStorage.setItem(CLIENT_PROPERTIES_KEY, JSON.stringify(__PROPS__));
		}
		// Otherwise, update the global properties with the stored values
		else {
			// Override global properties with stored values (const override is intentional for runtime config)
			Object.assign(__PROPS__, JSON.parse(storedProperties));
		}
		// console.warn("props after", __PROPS__);
	}
}

export const ModelStorage = new ModelStorageService();